import React, { memo, useCallback } from 'react';

import { Box, Button, CircularProgress, Typography } from '@mui/material';
import LabelCDS from '@sharedCR/coreDesignSystem/LabelCDS';
import { LabelSize } from '@sharedCR/coreDesignSystem/LabelCDS/LabelCDS.constants';
import FeeRecommendationChanges from 'app/shared/components/feeRecommendations/FeeRecommendationChanges';
import FeeRecommendationTitle from 'app/shared/components/feeRecommendations/FeeRecommendationTitle';
import PropTypes from 'prop-types';

import {
  FEE_RECOMMENDATION_CHANGE_TYPE_DISPLAY,
  FEE_RECOMMENDATION_ITEM_STATUS_DISPLAY,
  FeeRecommendationItemStatus,
} from 'constants/feeRecommendations';
import { getFeeRecommendationHeading } from 'utils/feeRecommendations';

import { LoanFeesTestIds } from '../../../../LoanFees.testIds';
import { buttonContentStyles } from '../../FeeRecommendationsPanel.styles';

import {
  actionsStyles,
  headerStyles,
  labelsStyles,
  reviewNotesStyles,
  rootStyles,
} from './FeeRecommendationItem.styles';

const FeeRecommendationItem = ({
  item,
  isDisabled = false,
  isAccepting = false,
  onAccept,
  onReject,
}) => {
  const changeType = FEE_RECOMMENDATION_CHANGE_TYPE_DISPLAY[item.changeType];
  const status = FEE_RECOMMENDATION_ITEM_STATUS_DISPLAY[item.status];
  const isPending = item.status === FeeRecommendationItemStatus.PENDING;
  const { title } = getFeeRecommendationHeading(item);

  const onClickAccept = useCallback(() => onAccept(item.id), [onAccept, item.id]);
  const onClickReject = useCallback(() => onReject(item.id, title), [onReject, item.id, title]);

  return (
    <Box sx={rootStyles}>
      <Box sx={headerStyles}>
        <Box sx={labelsStyles}>
          {changeType && (
            <LabelCDS size={LabelSize.S} title={changeType.label} variant={changeType.variant} />
          )}
          <FeeRecommendationTitle item={item} />
          {status && <LabelCDS size={LabelSize.S} title={status.label} variant={status.variant} />}
        </Box>

        {isPending && (
          <Box sx={actionsStyles}>
            <Button
              data-testid={LoanFeesTestIds.getFeeRecommendationRejectId(item.id)}
              disabled={isDisabled}
              size="small"
              variant="outlined"
              onClick={onClickReject}
            >
              Reject
            </Button>
            <Button
              data-testid={LoanFeesTestIds.getFeeRecommendationAcceptId(item.id)}
              disabled={isDisabled}
              size="small"
              variant="contained"
              onClick={onClickAccept}
            >
              <Box sx={buttonContentStyles}>
                {isAccepting && <CircularProgress color="inherit" size={16} />}
                Accept
              </Box>
            </Button>
          </Box>
        )}
      </Box>

      <FeeRecommendationChanges item={item} />

      {!!item.reviewNotes && (
        <Typography color="text.secondary" sx={reviewNotesStyles} variant="regular12">
          {`Review notes: ${item.reviewNotes}`}
        </Typography>
      )}
    </Box>
  );
};

FeeRecommendationItem.propTypes = {
  item: PropTypes.object.isRequired,
  onAccept: PropTypes.func.isRequired,
  onReject: PropTypes.func.isRequired,
  isAccepting: PropTypes.bool,
  isDisabled: PropTypes.bool,
};

export default memo(FeeRecommendationItem);
